import { defineStore, storeToRefs } from 'pinia';
import {
  arrayToMap,
  arrayToGroup,
  useRootStore,
  i18n,
} from '@qsrsoft/myq-core';
import { ref, computed, watch } from 'vue';
import {
  isString,
  isArray,
  isRecord,
  typeGuardGenerator,
  isBoolean,
} from '@metools/tcheck';

import {
  getCalendars,
  getCalendarSubscriptions,
  syncCalendarSubscriptions,
  getCommsHubOrgs,
  getSharedCommsHubOrgs,
  fetchRosterEvents,
  getFilteredRosterEvents,
} from '@/views/dashboard/widgets/calendar/api/api';
import {
  anniversaryCalendar,
  birthdayCalendar,
  orgCalendar,
} from '@/views/dashboard/widgets/calendar/constants/constants';
import { RemoteSubscriptionEvent } from '@/views/dashboard/widgets/calendar/models/events';
import { MyQCalendar } from '@/views/dashboard/widgets/calendar/models/myq_calendar';
import { useCommsHubComposable } from '@/views/dashboard/widgets/calendar/composables/commshub_composable';
import { usePermissions } from '@qsrsoft/myq-core';

/** @typedef {import('@/views/dashboard/widgets/calendar/models/events').MyQEvent} MyQEvent */
/** @typedef {import('@/views/dashboard/widgets/calendar/models/myq_calendar').RemoteSubscriptionCalendar} RemoteSubscriptionCalendar */

export const useCalendarStore = defineStore('calendar', () => {
  /** @type {import('vue').Ref<MyQCalendar[] >} */
  const calendarsState = ref([]);

  /** @type {import('vue').Ref<RemoteSubscriptionCalendar[]>} */
  const subscriptionCalendars = ref([]);

  /** @type {import('vue').Ref<MyQCalendar[]>} */
  const commsHubCalendarsState = ref([]);

  /** @type {import('vue').Ref<string[]>} */
  const commsHubOrgIdsState = ref([]);

  /** @type {import('vue').Ref<BirthdayAnniversaryAPIResponse[] | null>} */
  const rosterDataCache = ref(null);

  /** @type {import('vue').Ref<boolean>} */
  const rosterDataLoading = ref(false);

  const rootStore = useRootStore();
  const {
    orgId,
    userId,
    locations,
    orgName,
    userGroups,
    enterpriseClient,
    countryCode,
    isAdmin,
    isOrgAdmin,
  } = storeToRefs(rootStore);
  const { getUserSettings, updateUserSettings } = rootStore;
  const { isCommsHub } = useCommsHubComposable();

  /**
   * Reads a boolean user setting, tolerating the wrapped shapes the settings
   * API returns for these flags.
   * @param {unknown} raw The value getUserSettings returned
   * @param {string} key The setting name, used when the value arrives wrapped
   * @returns {boolean}
   */
  const AUTO_SELECTED_SETTING_KEY = 'commsHubCalendarsWereAutoSelected';

  function toBooleanSetting(raw, key) {
    if (isBoolean(raw)) {
      return raw;
    }
    if (isRecord(raw)) {
      return raw.value === true || raw[key] === true;
    }
    return false;
  }

  /** @type {import('vue').Ref<boolean>} */
  const selectedCalendarsInitialized = ref(false);

  /** @type {import('vue').Ref<string[]>} */
  const selectedCalendars = ref([]);

  const selectedCalendarsSet = computed(() => new Set(selectedCalendars.value));

  /**
   * Gets all selectable calendar IDs including org, birthdays, anniversaries, custom, and CommsHub
   * @returns {string[]}
   */
  function getAllSelectableCalendarIds() {
    const ids = ['', birthdayCalendar, anniversaryCalendar];

    // Add custom calendars
    for (const cal of calendarsState.value) {
      if (cal.id && !ids.includes(cal.id)) {
        ids.push(cal.id);
      }
    }

    // Add CommsHub calendars
    for (const cal of commsHubCalendarsState.value) {
      if (cal.id && !ids.includes(cal.id)) {
        ids.push(cal.id);
      }
    }

    return ids;
  }

  /**
   * Serialises writes of the `selectedCalendars` setting. Init persists in the
   * background while the CommsHub watcher can persist its own selection moments
   * later; chaining makes the last caller the last writer, so an earlier write
   * landing late cannot restore a stale list.
   * @type {Promise<unknown>}
   */
  let selectionWriteChain = Promise.resolve();

  /**
   * @param {string[]} calendars - The selection to store
   * @returns {Promise<unknown>} Resolves when this write has been attempted
   */
  function persistSelectedCalendars(calendars) {
    selectionWriteChain = selectionWriteChain
      .catch(() => {})
      .then(() => {
        return updateUserSettings('selectedCalendars', {
          selectedCalendars: calendars,
        });
      });

    return selectionWriteChain;
  }

  /**
   * Persists what init worked out, after it has already been applied.
   *
   * The auto-selected flag is written only once the selection write has
   * succeeded. A failed selection write therefore leaves the flag unset and the
   * auto-add path runs again on the next load, rather than leaving the stored
   * selection and the flag disagreeing with no way back.
   * @param {object} args
   * @param {string[]} args.calendars - The selection that was applied
   * @param {boolean} args.persistSelection - Whether the selection changed
   * @param {boolean} args.markAutoSelected - Whether to set the auto-add flag
   * @returns {Promise<void>} Resolves once the writes have been attempted
   */
  async function persistInitialSelection(args) {
    try {
      if (args.persistSelection) {
        await persistSelectedCalendars(args.calendars);
      }
      if (args.markAutoSelected) {
        await updateUserSettings(AUTO_SELECTED_SETTING_KEY, true);
      }
    } catch (e) {
      console.error('Error persisting calendar selection:', e);
    }
  }

  /**
   * Initializes selected calendars from user settings.
   * Handles defaults and CommsHub auto-add logic.
   */
  async function initSelectedCalendars() {
    if (selectedCalendarsInitialized.value) {
      return;
    }

    try {
      const settings = await getUserSettings('selectedCalendars');
      /** @type {string[]} */
      let calendars = [];

      // Persisting the selection must not gate rendering: events are already
      // loading and are filtered against this selection, so a slow settings
      // write would hold them off screen. Work out what needs storing, apply
      // the selection, then store it in the background.
      let persistSelection = false;
      let markAutoSelected = false;

      if (isCommsHub.value) {
        // Everything on at every load. The selector filters the session, so
        // there is no stored selection to read back or keep up to date.
        calendars = getAllSelectableCalendarIds();
      } else if (
        settings?.selectedCalendars &&
        Array.isArray(settings.selectedCalendars) &&
        settings.selectedCalendars.length > 0
      ) {
        calendars = settings.selectedCalendars;

        // Check if we need to auto-add CommsHub calendars
        const commsHubAutoSelectedRaw = await getUserSettings(
          AUTO_SELECTED_SETTING_KEY,
        );
        const commsHubAutoSelected = toBooleanSetting(
          commsHubAutoSelectedRaw,
          AUTO_SELECTED_SETTING_KEY,
        );

        const commsHubIds = commsHubCalendarsState.value.map((c) => c.id);

        // Discovery may land before this runs, so the hubs are added here as
        // well as in the watcher. Once only: a CommsHub viewer can turn them
        // off again in the External Organizations selector.
        if (!commsHubAutoSelected) {
          const missingCommsHub = commsHubIds.filter(
            (id) => !calendars.includes(id),
          );

          if (missingCommsHub.length > 0) {
            calendars = [...calendars, ...missingCommsHub];
            persistSelection = true;
          }
        }
        // Only set flag when we had CommsHub calendars to consider. If none were loaded yet,
        // leave flag false so the watcher will auto-add them when they load.
        if (!commsHubAutoSelected && commsHubIds.length > 0) {
          markAutoSelected = true;
        }
      } else {
        // No saved selection or empty - default to all
        calendars = getAllSelectableCalendarIds();
        persistSelection = true;
        // Only once hubs are in hand, as above: flagging them before they load
        // reads later as the viewer having turned them off.
        markAutoSelected = commsHubCalendarsState.value.length > 0;
      }

      selectedCalendars.value = calendars;
      selectedCalendarsInitialized.value = true;

      persistInitialSelection({
        calendars,
        persistSelection,
        markAutoSelected,
      });
    } catch (e) {
      console.error('Error initializing selected calendars:', e);
      selectedCalendars.value = getAllSelectableCalendarIds();
      selectedCalendarsInitialized.value = true;
    }
  }

  /**
   * Updates selected calendars and persists to user settings.
   * Called by CalendarSelector when user changes selection.
   * @param {string[]} calendars
   */
  async function setSelectedCalendars(calendars) {
    selectedCalendars.value = calendars;
    try {
      await persistSelectedCalendars(calendars);
    } catch (e) {
      console.error('Error saving selected calendars:', e);
    }
  }

  // Watch for new CommsHub calendars loading after init
  watch(
    () => commsHubCalendarsState.value.length,
    async (newCount, oldCount) => {
      if (!selectedCalendarsInitialized.value) {
        return;
      }
      if (newCount > (oldCount ?? 0)) {
        // New CommsHub calendars appeared
        const commsHubAutoSelectedRaw = await getUserSettings(
          AUTO_SELECTED_SETTING_KEY,
        );
        const commsHubAutoSelected = toBooleanSetting(
          commsHubAutoSelectedRaw,
          AUTO_SELECTED_SETTING_KEY,
        );

        // Check if user has all regular calendars selected (treat as "all on").
        // Compare by membership, not count: a stale id left in the saved
        // selection by a since-deleted calendar must not read as "some off".
        const regularIds = getAllSelectableCalendarIds().filter(
          (id) => !id.startsWith('commshub-org-'),
        );
        const userWantsAllOn = regularIds.every((id) =>
          selectedCalendars.value.includes(id),
        );

        // The all-on shortcut is for regular orgs only. A CommsHub viewer's
        // regular set is effectively just its own org calendar, so the
        // shortcut would always fire and undo every deselection it makes in
        // the External Organizations selector.
        if (isCommsHub.value || !commsHubAutoSelected || userWantsAllOn) {
          const commsHubIds = commsHubCalendarsState.value.map((c) => c.id);
          const missingCommsHub = commsHubIds.filter(
            (id) => !selectedCalendars.value.includes(id),
          );

          if (missingCommsHub.length > 0) {
            const newSelection = [
              ...selectedCalendars.value,
              ...missingCommsHub,
            ];
            await setSelectedCalendars(newSelection);
          }

          if (!isCommsHub.value && !commsHubAutoSelected) {
            await updateUserSettings(AUTO_SELECTED_SETTING_KEY, true);
          }
        }
      }
    },
  );

  // Watch for new regular calendars loading after init
  watch(
    () => calendarsState.value.length,
    async (newCount, oldCount) => {
      if (!selectedCalendarsInitialized.value) {
        return;
      }
      if (newCount > (oldCount ?? 0) && oldCount !== undefined) {
        // New regular calendars appeared - add if user previously had all selected
        const regularIds = getAllSelectableCalendarIds().filter(
          (id) => !id.startsWith('commshub-org-'),
        );
        const previousRegularCount = oldCount;
        const selectedRegular = selectedCalendars.value.filter(
          (id) => !id.startsWith('commshub-org-'),
        );

        // If user previously had all regular calendars selected, add new ones
        if (selectedRegular.length >= previousRegularCount) {
          const missingRegular = regularIds.filter(
            (id) => !selectedCalendars.value.includes(id),
          );

          if (missingRegular.length > 0) {
            const newSelection = [
              ...selectedCalendars.value,
              ...missingRegular,
            ];
            await setSelectedCalendars(newSelection);
          }
        }
      }
    },
  );

  const startDate = ref(new Date());

  const { orgHasApp: hasCalPermissionCrewPortal } =
    usePermissions('crew_portal');
  const { orgHasApp: hasCalPermissionMyqOps } = usePermissions('myq_ops');
  const { orgHasApp: orgHasCalendarApp } = usePermissions('calendar');
  const { orgHasApp: orgHasFeedPage } = usePermissions('feed_page');
  const { hasPermission: canEditCalPermission } = usePermissions(
    'calendar_edit_calendars',
  );
  const { hasPermission: canCreateEventsPermission } = usePermissions(
    'calendar_edit_event',
  );
  const { hasPermission: canManageBulletins } =
    usePermissions('bulletins_manager');

  const isMcd = computed(() => {
    return enterpriseClient.value?.toLowerCase() === 'mcdonalds';
  });

  const isUSMcd = computed(() => {
    return isMcd.value && countryCode.value?.toLowerCase() === 'us';
  });

  const orgHasPremiumCalendar = computed(() => {
    return hasCalPermissionCrewPortal.value || hasCalPermissionMyqOps.value;
  });

  const hasFeedPostAccess = computed(() => {
    if (!canManageBulletins.value) {
      return false;
    }

    if (isCommsHub.value) {
      return true;
    }
    if (isUSMcd.value) {
      return hasCalPermissionCrewPortal.value;
    }
    return orgHasFeedPage.value;
  });

  const hasCalendarAccess = computed(() => {
    if (isCommsHub.value) {
      return true;
    }
    if (isUSMcd.value) {
      return orgHasPremiumCalendar.value;
    }
    return orgHasCalendarApp.value;
  });

  const nonPremiumCalendar = computed(() => {
    return isUSMcd.value && !hasCalendarAccess.value;
  });

  const calendarVisible = computed(() => {
    return hasCalendarAccess.value || nonPremiumCalendar.value;
  });

  const isSystemAdmin = computed(() => !!(isAdmin.value || isOrgAdmin.value));

  // Only sys admins see premium upsells; everyone else gets the free calendar
  const showPremiumUpsell = computed(
    () => nonPremiumCalendar.value && isSystemAdmin.value,
  );

  const canShareCalendars = computed(
    () => hasCalendarAccess.value || showPremiumUpsell.value,
  );

  /** Role groupIds that should have edit/create access. */
  const editAccessRoles = computed(() => {
    return [
      'directorOfOperations',
      'operationsManager',
      'generalManager',
      'supervisor',
      'ownerOperator',
    ];
  });

  /** Normalized role display names for matching (lowercase). */
  const editAccessRoleNames = computed(() => {
    return [
      'director of operations',
      'operations manager',
      'general manager',
      'supervisor',
      'owner operator',
    ];
  });

  /**
   * Ensures roster data is loaded (fetches once, caches).
   * @param {string[]} locations
   */
  async function loadRosterData(locations) {
    if (rosterDataCache.value) {
      return rosterDataCache.value;
    }

    if (rosterDataLoading.value) {
      while (rosterDataLoading.value) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return rosterDataCache.value ?? [];
    }

    rosterDataLoading.value = true;
    try {
      rosterDataCache.value = await fetchRosterEvents({
        orgId: orgId.value,
        locations,
        startDate: '2000-01-01',
        endDate: '2100-12-31',
      });
      return rosterDataCache.value;
    } catch (e) {
      console.error('Error fetching roster data:', e);
      return [];
    } finally {
      rosterDataLoading.value = false;
    }
  }

  /**
   * Gets filtered roster events for a date range.
   * @param {object} args
   * @param {string} args.startDate
   * @param {string} args.endDate
   * @param {string[]} args.locations
   */
  async function loadRosterEventsForDateRange(args) {
    const rosterData = await loadRosterData(args.locations);
    return getFilteredRosterEvents({
      rosterData,
      orgId: orgId.value,
      startDate: args.startDate,
      endDate: args.endDate,
    });
  }

  function clearRosterCache() {
    rosterDataCache.value = null;
  }

  /** True if the current user has at least one role in editAccessRoles (by groupId or name). */
  const userHasEditAccessByRole = computed(() => {
    const groups = userGroups.value;
    if (!isArray(groups) || groups.length === 0) {
      return false;
    }

    const roleIdSet = new Set(
      editAccessRoles.value.map(function toLowerCase(r) {
        return r.toLowerCase();
      }),
    );
    const roleNameSet = new Set(editAccessRoleNames.value);

    return groups.some(function checkGroup(g) {
      if (!g) {
        return false;
      }
      const id = isString(g.groupId) ? g.groupId.toLowerCase() : '';
      const name = isString(g.name) ? g.name.toLowerCase().trim() : '';
      return roleIdSet.has(id) || roleNameSet.has(name);
    });
  });

  const canEditCalendars = computed(() => {
    return canEditCalPermission.value && hasCalendarAccess.value;
  });

  const nonPremiumEditCalendars = computed(() => {
    return (
      (canEditCalPermission.value || userHasEditAccessByRole.value) &&
      showPremiumUpsell.value
    );
  });

  const canCreateEvents = computed(() => {
    return canCreateEventsPermission.value && hasCalendarAccess.value;
  });

  const nonPremiumCreateEvents = computed(() => {
    return (
      (canCreateEventsPermission.value || userHasEditAccessByRole.value) &&
      showPremiumUpsell.value
    );
  });

  /**
   * Determines the color for a CommsHub calendar based on organization name
   * @param {string} orgName - The organization name
   * @returns {string} - The color value for the calendar
   */
  function getCommsHubCalendarColor(orgName) {
    if (!orgName) {
      return 'rgb(var(--v-theme-surface-deep-purple-high))';
    }
    const upperName = orgName.toUpperCase();

    if (upperName.startsWith('RMHC')) {
      return 'rgb(var(--v-theme-success))';
    }
    if (upperName.includes('NABIT')) {
      return 'rgb(var(--v-theme-surface-cyan-high))';
    }
    if (upperName.includes('BU ') || upperName.startsWith('BU')) {
      return 'rgb(var(--v-theme-surface-deep-purple-high))';
    }
    return 'rgb(var(--v-theme-surface-deep-purple-high))';
  }

  const calendarMap = computed(() => {
    const allCalendars = [];

    // Add regular calendars
    if (calendarsState.value) {
      allCalendars.push(...calendarsState.value);
    }

    // Add CommsHub calendars
    if (commsHubCalendarsState.value.length > 0) {
      allCalendars.push(...commsHubCalendarsState.value);
    }

    if (hasCalendarAccess.value) {
      allCalendars.push(
        new MyQCalendar({
          id: '',
          orgId: orgId.value,
          title: isCommsHub.value
            ? orgName.value
            : i18n.global.t('organization_calendar'),
          color: 'rgb(var(--v-theme-primary))',
          active: true,
          createdTime: new Date().toISOString(),
          createdByUserId: 'system',
          lastEditedTime: new Date().toISOString(),
          lastEditedByUserId: 'system',
          roleAccessList: ['*'],
          locationAccessList: ['*'],
        }),
      );
    }

    if (!isCommsHub.value) {
      allCalendars.push(
        new MyQCalendar({
          id: birthdayCalendar,
          orgId: orgId.value,
          title: i18n.global.t('birthdays'),
          color: 'rgb(var(--v-theme-surface-amber-low))',
          active: true,
          createdTime: new Date().toISOString(),
          createdByUserId: 'system',
          lastEditedTime: new Date().toISOString(),
          lastEditedByUserId: 'system',
          roleAccessList: ['*'],
          locationAccessList: ['*'],
        }),
      );

      allCalendars.push(
        new MyQCalendar({
          id: anniversaryCalendar,
          color: 'rgb(var(--v-theme-surface-purple-low))',
          orgId: orgId.value,
          title: i18n.global.t('work_anniversaries'),
          active: true,
          createdTime: new Date().toISOString(),
          createdByUserId: 'system',
          lastEditedTime: new Date().toISOString(),
          lastEditedByUserId: 'system',
          roleAccessList: ['*'],
          locationAccessList: ['*'],
        }),
      );
    }

    return arrayToMap(allCalendars, (calendar) => calendar.id);
  });

  const calendars = computed(() => {
    const cals = { ...calendarMap.value };

    const orgCal = cals[''];
    const birthdayCal = cals[birthdayCalendar];
    const anniversaryCal = cals[anniversaryCalendar];

    delete cals[''];
    delete cals[birthdayCalendar];
    delete cals[anniversaryCalendar];

    /** @type {MyQCalendar[]} */
    const commsHubCals = [];
    for (const calId in cals) {
      if (cals[calId]?.id.startsWith('commshub-org-')) {
        commsHubCals.push(cals[calId]);
        delete cals[calId];
      }
    }

    /** @type {MyQCalendar[]} */
    const result = [];

    if (orgCal) {
      result.push(orgCal);
    }

    result.push(
      ...Object.values(cals)
        .filter((cal) => cal.id)
        .sort((a, b) => a.title.localeCompare(b.title)),
    );

    if (birthdayCal) {
      result.push(birthdayCal);
    }

    if (anniversaryCal) {
      result.push(anniversaryCal);
    }

    result.push(...commsHubCals);

    return result;
  });

  const subscriptionCalendarMap = computed(() => {
    return arrayToMap(subscriptionCalendars.value ?? [], (sub) => sub.id);
  });

  // Group subscriptions by their calendar ID for easy lookup
  const subscriptionsByCalendarId = computed(() => {
    return arrayToGroup(
      subscriptionCalendars.value ?? [],
      (sub) => sub.myqCalendarId,
    );
  });

  const subscriptionIdToParentMap = computed(() => {
    return arrayToMap(subscriptionCalendars.value ?? [], (sub) => sub.id);
  });

  /**
   *
   * @param {MyQEvent | RemoteSubscriptionEvent} event
   * @returns {MyQCalendar | undefined} the calendar tied to the event or undefined
   */
  function getCalendarFromEvent(event) {
    let calendarId = event.calendarId;

    // If it's a subscription event, find the parent calendar
    if (event instanceof RemoteSubscriptionEvent) {
      const subscription =
        subscriptionIdToParentMap.value[event.subscriptionCalendarId];
      if (subscription) {
        calendarId = subscription.myqCalendarId; // Use parent calendar's myqCalendarId
      }
    }

    return calendarMap.value[calendarId ?? ''];
  }

  /** @type {import('vue').Ref<import('@/views/dashboard/widgets/calendar/api/api').CommsHubOrg[]>} */
  const commsHubOrgs = ref([]);

  /**
   * @param {string[]} locationIds Location IDs for which we're retrieving Comms Hub orgs
   */
  async function handleGetCommsHubOrgs(locationIds) {
    if (locationIds.length === 0 && commsHubOrgs.value.length > 0) {
      return [];
    }
    const orgs = await getCommsHubOrgs({ orgId: orgId.value, locationIds });
    commsHubOrgs.value = orgs;
    return orgs;
  }

  /** @typedef {object} Location
   * @property {boolean} isActive
   * @property {string} locationId
   * @property {string} locationStatus
   * @property {string} name
   * @property {string} nameValue
   * @property {string} value
   */

  /** @type {import('@metools/tcheck').TypeGuard<Location>} */
  const locTypeCheck = typeGuardGenerator({
    isActive: isBoolean,
    locationId: isString,
    locationStatus: isString,
    name: isString,
    nameValue: isString,
    value: isString,
  });

  /**
   * Finds the CommsHub orgs whose content this org can see and creates a
   * MyQCalendar for each, added to commsHubCalendarsState.
   *
   * For a regular org the sources are the hubs covering its locations; for a
   * CommsHub — which has no location membership pointing at other hubs — the
   * sources are the hubs sharing content into it.
   */
  async function createCommsHubCalendars() {
    if (!isCommsHub.value && !isMcd.value) {
      return;
    }
    if (
      commsHubCalendarsState.value.length > 0 &&
      commsHubOrgs.value.length > 0
    ) {
      return;
    }

    /** @type {import('../api/api').CommsHubOrg[]} */
    let hubOrgs = [];
    if (isCommsHub.value) {
      hubOrgs = await getSharedCommsHubOrgs(orgId.value);
      commsHubOrgs.value = hubOrgs;
    } else {
      if (
        !locations.value ||
        locations.value.length === 0 ||
        !isArray(locations.value)
      ) {
        return;
      }
      const validLocations = locations.value.filter(locTypeCheck);
      const locationIds = validLocations.map((loc) => loc.locationId);
      hubOrgs = await handleGetCommsHubOrgs(locationIds);
    }

    if (hubOrgs.length > 0) {
      const commsHubOrgIds = hubOrgs.map((org) =>
        org.orgId.replace('org-', ''),
      );
      commsHubOrgIdsState.value = commsHubOrgIds;

      commsHubCalendarsState.value = [];

      for (const org of hubOrgs) {
        const cleanedOrgId = org.orgId.replace('org-', '');
        const orgName = org.orgName;

        // To a franchisee this is "the CommsHub". To another hub these are
        // listed under their own heading, so the org name stands alone.
        const title = isCommsHub.value ? orgName : `(CommsHub) ${orgName}`;

        const newCalendar = new MyQCalendar({
          id: `commshub-org-${cleanedOrgId}`,
          orgId: cleanedOrgId,
          title,
          color: getCommsHubCalendarColor(orgName),
          active: true,
          createdTime: new Date().toISOString(),
          createdByUserId: 'system',
          lastEditedTime: new Date().toISOString(),
          lastEditedByUserId: 'system',
          roleAccessList: ['*'],
          locationAccessList: ['*'],
          isCommsHubCalendar: true,
          orgName,
        });

        commsHubCalendarsState.value.push(newCalendar);
      }
    }
  }

  /**
   * @param {object} [opts]
   * @param {boolean} opts.forceSync
   */
  async function fetchCalendars(opts) {
    const forceSync = opts?.forceSync ?? false;

    if (calendarsState.value.length > 0 && !forceSync) {
      return;
    }

    if (!isString(orgId.value) || !isString(userId.value)) {
      console.warn('Invalid orgId or userId');
      return;
    }
    if (hasCalendarAccess.value) {
      calendarsState.value = await getCalendars({
        orgId: orgId.value,
        userId: userId.value,
      });
    }

    await initSelectedCalendars();
  }

  async function fetchSubscriptionCalendars() {
    try {
      const subscriptions = await getCalendarSubscriptions(orgId.value);
      setSubscriptionCalendars(subscriptions);
    } catch (error) {
      console.error('Error loading subscription calendars:', error);
    }
  }

  /**
   *
   * @param {RemoteSubscriptionCalendar[]} subscriptions
   */
  function setSubscriptionCalendars(subscriptions) {
    subscriptionCalendars.value = subscriptions;
  }

  /**
   *
   * @param {object} args Arguments
   * @param {string} args.orgId Organization ID
   * @param {string} args.subscriptionId Subscription ID
   */
  async function forceSyncCalendarSubscription(args) {
    const { orgId, subscriptionId } = args;
    await syncCalendarSubscriptions({ orgId, subscriptionId });
  }

  return {
    fetchCalendars,
    setSubscriptionCalendars,
    fetchSubscriptionCalendars,
    getCalendarFromEvent,

    forceSyncCalendarSubscription,

    // Refs & Computed Refs
    calendars,
    subscriptionCalendars,
    startDate,
    calendarMap,
    subscriptionCalendarMap,
    subscriptionsByCalendarId,
    orgCalendar,
    calendarsState,
    commsHubCalendarsState,
    commsHubOrgIdsState,
    hasCalendarAccess,
    canEditCalendars,
    canCreateEvents,
    createCommsHubCalendars,
    nonPremiumEditCalendars,
    nonPremiumCreateEvents,
    nonPremiumCalendar,
    calendarVisible,
    showPremiumUpsell,
    canShareCalendars,
    loadRosterData,
    loadRosterEventsForDateRange,
    clearRosterCache,
    setSelectedCalendars,
    selectedCalendars,
    selectedCalendarsSet,
    hasFeedPostAccess,
  };
});
